/**
 * SanPlumb — Dotación / Volumetría (Fase 3c)
 *
 * Pantalla de cálculo de dotación diaria (AF + AC) y volumetría de
 * almacenamiento (cisterna, tanque elevado, terma), más un corte
 * arquitectónico esquemático que visualiza los volúmenes a escala.
 *
 * TODO el cálculo viene del módulo normativo `modules/dotacion.ts`
 * (NO la fórmula aproximada del diseño — ver nota §I v1.4).
 *
 * Diferido a sub-fase posterior: catálogo flotante de selección de
 * depósitos comerciales (cisterna/tanque/terma por marca).
 *
 * Portado de `extras.jsx` (Dotacion) del diseño aprobado.
 */

import { useState } from 'react';
import type { BuildingPressureResult, TipoEdificacion } from '../../modules/dotacion';
import { calcBuildingPressure, calcDotacion } from '../../modules/dotacion';
import { SESSION_KEYS } from '../../engine/sessionState';
import { useSessionState } from '../../hooks/useSessionState';
import {
  CATALOGO_DEPOSITOS,
  DEPOSITO_DEFAULTS,
  type DepositoItem,
  type DepositoTipo,
} from '../../constants/depositos';
import {
  DOTACION_AF_MULTIFAMILIAR,
  DOTACION_AF_UNIFAMILIAR,
  DOTACION_AC,
  DOTACION_AC_EXTRA_POR_DORM,
} from '../../constants/is010';
import { DotIcon } from '../Icon';

interface DotacionForm {
  tipo: TipoEdificacion;
  pisos: number;
  dptos: number;
  dorm: number;
  pisosUni: number;
  alturaPiso: number;
  area: number;
  dormUni: number;
}

const DOTACION_DEFAULTS: DotacionForm = {
  tipo: 'multi',
  pisos: 4,
  dptos: 2,
  dorm: 3,
  pisosUni: 2,
  alturaPiso: 2.7,
  area: 200,
  dormUni: 3,
};

function Counter({
  value,
  set,
  min,
  max,
}: {
  value: number;
  set: (v: number) => void;
  min: number;
  max: number;
}): JSX.Element {
  const clamp = (v: number): number => Math.max(min, Math.min(max, v));
  return (
    <div className="counter">
      <button onClick={() => set(clamp(value - 1))}>−</button>
      <span className="val">{value}</span>
      <button onClick={() => set(clamp(value + 1))}>+</button>
    </div>
  );
}

export function Dotacion(): JSX.Element {
  const [form, setForm] = useSessionState<DotacionForm>(
    SESSION_KEYS.DOTACION,
    DOTACION_DEFAULTS,
  );
  const { tipo, pisos, dptos, dorm, pisosUni, alturaPiso, area, dormUni } = form;
  function patch(p: Partial<DotacionForm>): void {
    setForm((f) => ({ ...f, ...p }));
  }
  const setTipo = (v: TipoEdificacion): void => patch({ tipo: v });
  const setPisos = (v: number): void => patch({ pisos: v });
  const setDptos = (v: number): void => patch({ dptos: v });
  const setDorm = (v: number): void => patch({ dorm: v });
  const setPisosUni = (v: number): void => patch({ pisosUni: v });
  const setAlturaPiso = (v: number): void => patch({ alturaPiso: v });
  const setArea = (v: number): void => patch({ area: v });
  const setDormUni = (v: number): void => patch({ dormUni: v });
  const [tableOpen, setTableOpen] = useState(false);

  // Catálogo de depósitos (cisterna / tanque / terma por marca)
  const [showCatalog, setShowCatalog] = useState<DepositoTipo | null>(null);
  const [deposito, setDeposito] = useSessionState<Record<DepositoTipo, string>>(
    SESSION_KEYS.DEPOSITOS,
    { ...DEPOSITO_DEFAULTS },
  );
  function selectDeposito(tipoDep: DepositoTipo, id: string): void {
    setDeposito((d) => ({ ...d, [tipoDep]: id }));
  }

  const r = calcDotacion({ tipo, pisos, dptos, dorm, areaLote: area, dormUni });

  const dotM3 = r.dotacionAF_L / 1000;
  const cisternaM3 = +(r.cisterna_L / 1000).toFixed(2);
  const tanqueM3 = +(r.tanque_L / 1000).toFixed(2);
  const termaM3 = +(r.terma_L / 1000).toFixed(3);
  const requiredFor = (t: DepositoTipo): number =>
    t === 'cisterna' ? cisternaM3 : t === 'tanque' ? tanqueM3 : termaM3;
  const dormPerUnit = tipo === 'multi' ? dorm : dormUni;
  const totalPisosNum = tipo === 'multi' ? pisos : pisosUni;
  const alturaPisoNum = tipo === 'multi' ? 2.7 : alturaPiso;
  const buildingTopM = (totalPisosNum * alturaPisoNum).toFixed(2);
  const pressure = calcBuildingPressure({
    totalPisos: totalPisosNum,
    alturaPiso_m: alturaPisoNum,
  });

  // ── Geometría del corte SVG ──────────────────────────────────────
  const VW = 720;
  const VH = 540;
  const pad = { l: 80, r: 90, t: 30, b: 30 };
  const totalPisos = tipo === 'multi' ? pisos : pisosUni;
  const floorH = Math.max(34, Math.min(78, 320 / totalPisos));
  const buildH = floorH * totalPisos;
  const azoteaH = 70;
  const groundY = pad.t + azoteaH + buildH;
  const cisternH = 40 + Math.min(80, cisternaM3 * 20);
  const tanqueH = 30 + Math.min(60, tanqueM3 * 22);
  const xMid = (VW - pad.l - pad.r) / 2 + pad.l;
  const bWidth = 280;
  const bLeft = xMid - bWidth / 2;
  const cisX = bLeft + 60;
  const cisW = 160;
  const tkX = bLeft + 30;
  const tkW = 120;

  return (
    <div className="dotacion screen-anim" data-screen-label="04 Dotación / Volumetría">
      <div className="dot-form">
        <div className="eyebrow">PASO 04 · DOTACIÓN + VOLUMETRÍA</div>
        <div className="h-title" style={{ marginTop: 10 }}>
          Cisterna · Tanque · Azotea
        </div>
        <div className="h-sub">
          IS.010 §2.2 y §2.4 — cálculo automático por tipo de edificación.
        </div>

        <div className="section-h">Tipo de edificación</div>
        <div className="dot-tabs">
          <button
            className={`dot-tab ${tipo === 'multi' ? 'active' : ''}`}
            onClick={() => setTipo('multi')}
          >
            MULTIFAMILIAR
          </button>
          <button
            className={`dot-tab ${tipo === 'uni' ? 'active' : ''}`}
            onClick={() => setTipo('uni')}
          >
            UNIFAMILIAR
          </button>
        </div>

        {tipo === 'multi' ? (
          <>
            <div className="dot-row">
              <span className="k">
                <span className="k-ic">{DotIcon.building}</span>Número de pisos
              </span>
              <Counter value={pisos} set={setPisos} min={1} max={12} />
            </div>
            <div className="dot-row">
              <span className="k">
                <span className="k-ic">{DotIcon.door}</span>Dptos por piso
              </span>
              <Counter value={dptos} set={setDptos} min={1} max={8} />
            </div>
            <div className="dot-row">
              <span className="k">
                <span className="k-ic">{DotIcon.bed}</span>Dormitorios / dpto
              </span>
              <Counter value={dorm} set={setDorm} min={1} max={15} />
            </div>
          </>
        ) : (
          <>
            <div className="dot-row">
              <span className="k">
                <span className="k-ic">{DotIcon.area}</span>Área del lote
              </span>
              <span>
                <input
                  className="form-input wide"
                  value={area}
                  onChange={(e) => setArea(Number(e.target.value) || 0)}
                />{' '}
                m²
              </span>
            </div>
            <div className="dot-row">
              <span className="k">
                <span className="k-ic">{DotIcon.building}</span>Número de pisos
              </span>
              <Counter value={pisosUni} set={setPisosUni} min={1} max={5} />
            </div>
            <div className="dot-row">
              <span className="k">
                <span className="k-ic">{DotIcon.heightArr}</span>Altura piso estándar
              </span>
              <span>
                <input
                  className="form-input"
                  value={alturaPiso}
                  onChange={(e) => setAlturaPiso(Number(e.target.value) || 2.7)}
                  step="0.1"
                />{' '}
                m
              </span>
            </div>
            <div className="dot-row">
              <span className="k">
                <span className="k-ic">{DotIcon.bed}</span>Dormitorios totales
              </span>
              <Counter value={dormUni} set={setDormUni} min={1} max={10} />
            </div>
          </>
        )}

        <div className="section-h" style={{ marginTop: 18 }}>
          Resultados (IS.010)
        </div>
        <div className="result">
          <div className="kv">
            <span className="k">
              <span className="k-ic">{DotIcon.drop}</span>Dotación diaria AF
            </span>
            <span className="v big">{dotM3.toFixed(2)} m³/día</span>
          </div>
          <div className="kv">
            <span className="k">
              <span className="k-ic">{DotIcon.cistern}</span>Cisterna (≥¾ dot.)
            </span>
            <span className="v t-af">{cisternaM3} m³</span>
          </div>
          <div className="kv">
            <span className="k">
              <span className="k-ic">{DotIcon.tank}</span>Tanque elevado (≥⅓ dot.)
            </span>
            <span className="v t-ac">{tanqueM3} m³</span>
          </div>
          <div className="kv">
            <span className="k">
              <span className="k-ic">{DotIcon.door}</span>Viviendas
            </span>
            <span className="v">{r.totalViviendas}</span>
          </div>
          <div className="kv">
            <span className="k">
              <span className="k-ic">{DotIcon.metric}</span>Altura edificio
            </span>
            <span className="v">{buildingTopM} m</span>
          </div>
        </div>

        <div className="ac-result-card">
          <div className="ac-card-h">
            <span className="ac-dot" /> AGUA CALIENTE · TERMA
          </div>
          <div className="kv ac">
            <span className="k">Dotación AC ({dormPerUnit} dorm)</span>
            <span className="v">{Math.round(r.dotacionAC_L / r.totalViviendas)} L/día</span>
          </div>
          <div className="kv ac">
            <span className="k">
              AC total ({r.totalViviendas} {r.totalViviendas === 1 ? 'unid.' : 'dptos'})
            </span>
            <span className="v">{r.dotacionAC_L} L/día</span>
          </div>
          <div className="kv ac">
            <span className="k">
              <span className="k-ic">{DotIcon.terma}</span>Capacidad terma (⅕ AC)
            </span>
            <span className="v big t-ac">{r.terma_L} L</span>
          </div>
        </div>

        <PressureCard pressure={pressure} />

        <button className="src-btn" onClick={() => setTableOpen((o) => !o)}>
          <span>{tableOpen ? '▾' : '▸'}</span>
          Ver fórmula aplicada
        </button>
        {tableOpen && (
          <FormulaPanel
            tipo={tipo}
            dorm={dormPerUnit}
            area={area}
            totalViviendas={r.totalViviendas}
            dotacionAF={r.dotacionAF_L}
            cisternaL={r.cisterna_L}
            tanqueL={r.tanque_L}
            dotacionAC={r.dotacionAC_L}
            termaL={r.terma_L}
          />
        )}
      </div>

      <div className="dot-svg-wrap">
        <div className="dot-svg-hd">
          <div className="dot-svg-title">▸ Corte arquitectónico — esquema vertical</div>
          <div className="catalog-bar">
            {(['cisterna', 'tanque', 'terma'] as const).map((t) => (
              <button
                key={t}
                className={`cat-btn ${showCatalog === t ? 'on' : ''}`}
                onClick={() => setShowCatalog(showCatalog === t ? null : t)}
              >
                <span className="cat-btn-ic">
                  {t === 'cisterna' ? DotIcon.cistern : t === 'tanque' ? DotIcon.tank : DotIcon.terma}
                </span>{' '}
                {t.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {showCatalog && (
          <CatalogFloater
            tipo={showCatalog}
            selectedId={deposito[showCatalog]}
            required={requiredFor(showCatalog)}
            onSelect={(id) => selectDeposito(showCatalog, id)}
            onClose={() => setShowCatalog(null)}
          />
        )}

        <div className="dot-svg-canvas">
          <svg viewBox={`0 0 ${VW} ${VH}`} className="dot-svg">
            <defs>
              <pattern
                id="hatchBlue"
                patternUnits="userSpaceOnUse"
                width="6"
                height="6"
                patternTransform="rotate(45)"
              >
                <line x1="0" y1="0" x2="0" y2="6" stroke="#00C2FF" strokeWidth="1" opacity="0.5" />
              </pattern>
              <pattern
                id="hatchRed"
                patternUnits="userSpaceOnUse"
                width="6"
                height="6"
                patternTransform="rotate(45)"
              >
                <line x1="0" y1="0" x2="0" y2="6" stroke="#FF4040" strokeWidth="1" opacity="0.55" />
              </pattern>
              <marker
                id="arrUp"
                viewBox="0 0 10 10"
                refX="5"
                refY="0"
                markerWidth="6"
                markerHeight="6"
                orient="auto"
              >
                <path d="M0 8 L5 0 L10 8" fill="none" stroke="#00C2FF" strokeWidth="1.4" />
              </marker>
              <clipPath id="tkClip">
                <rect x={tkX} y={pad.t + azoteaH - tanqueH} width={tkW} height={tanqueH} />
              </clipPath>
              <clipPath id="cisClip">
                <rect x={cisX} y={groundY} width={cisW} height={cisternH} />
              </clipPath>
            </defs>

            {/* Azotea: tanque + terma */}
            <rect
              x={tkX}
              y={pad.t + azoteaH - tanqueH}
              width={tkW}
              height={tanqueH}
              fill="url(#hatchRed)"
              stroke="#FF4040"
              strokeWidth="1.2"
            />
            {/* Agua animada dentro del tanque (mismo patrón largo que la
                cisterna para que se vean olas fluyendo, no bloques). */}
            <g clipPath="url(#tkClip)">
              <path
                className="water wave1"
                d={`M ${tkX - 80} ${pad.t + azoteaH - tanqueH * 0.4} ${'q 20 -8 40 0 t 40 0 '.repeat(8)}V ${pad.t + azoteaH} H ${tkX - 80} Z`}
                fill="rgba(125,211,252,0.38)"
              />
              <path
                className="water wave2"
                d={`M ${tkX - 80} ${pad.t + azoteaH - tanqueH * 0.45} ${'q 20 -6 40 0 t 40 0 '.repeat(8)}V ${pad.t + azoteaH} H ${tkX - 80} Z`}
                fill="rgba(255,64,64,0.18)"
              />
            </g>
            <text
              x={tkX + tkW / 2}
              y={pad.t + azoteaH - tanqueH / 2 + 4}
              textAnchor="middle"
              fill="#FF4040"
              fontFamily="JetBrains Mono"
              fontSize="11"
            >
              {tanqueM3} m³
            </text>
            <text
              x={tkX + tkW / 2}
              y={pad.t + azoteaH + 14}
              textAnchor="middle"
              fill="#FF4040"
              fontFamily="JetBrains Mono"
              fontSize="9"
              letterSpacing="1.5"
            >
              TANQUE
            </text>

            <rect
              x={bLeft + 175}
              y={pad.t + azoteaH - 50}
              width="60"
              height="50"
              fill="rgba(139,92,246,0.15)"
              stroke="#8B5CF6"
              strokeWidth="1.2"
            />
            <text
              x={bLeft + 205}
              y={pad.t + azoteaH - 30}
              textAnchor="middle"
              fill="#8B5CF6"
              fontFamily="JetBrains Mono"
              fontSize="9"
            >
              TERMA
            </text>
            <text
              x={bLeft + 205}
              y={pad.t + azoteaH - 16}
              textAnchor="middle"
              fill="#8B5CF6"
              fontFamily="JetBrains Mono"
              fontSize="8"
            >
              {r.terma_L}L
            </text>

            {/* Pisos */}
            {Array.from({ length: totalPisos }).map((_, i) => {
              const y = pad.t + azoteaH + i * floorH;
              return (
                <g key={i}>
                  <rect
                    x={bLeft}
                    y={y}
                    width={bWidth}
                    height={floorH}
                    fill="rgba(15, 27, 43, 0.5)"
                    stroke="rgba(184,207,224,0.25)"
                    strokeWidth="1"
                  />
                  <text
                    x={bLeft + bWidth / 2}
                    y={y + floorH / 2 + 4}
                    textAnchor="middle"
                    fill="rgba(184,207,224,0.55)"
                    fontFamily="JetBrains Mono"
                    fontSize="10"
                    letterSpacing="2"
                  >
                    PISO {totalPisos - i}
                  </text>
                </g>
              );
            })}

            {/* NPT ±0.00 */}
            <line
              x1={pad.l - 30}
              y1={groundY}
              x2={VW - pad.r + 30}
              y2={groundY}
              stroke="#FFD60A"
              strokeWidth="1.4"
              strokeDasharray="6 3"
            />
            <text
              x={VW - pad.r + 36}
              y={groundY + 4}
              fill="#FFD60A"
              fontFamily="JetBrains Mono"
              fontSize="9"
            >
              NPT ±0.00
            </text>

            {/* Cisterna */}
            <rect
              x={cisX}
              y={groundY}
              width={cisW}
              height={cisternH}
              fill="url(#hatchBlue)"
              stroke="#00C2FF"
              strokeWidth="1.4"
            />
            {/* Agua animada dentro de la cisterna.
                El path se extiende ~3× el ancho del clipPath con muchas
                ondas; la traslación CSS de -40 px cae en el medio del
                path y nunca expone el borde. Resultado: olas que parecen
                fluir en lugar de "moverse en bloque a la izquierda". */}
            <g clipPath="url(#cisClip)">
              <path
                className="water wave1"
                d={`M ${cisX - 80} ${groundY + cisternH * 0.4} ${'q 20 -8 40 0 t 40 0 '.repeat(8)}V ${groundY + cisternH} H ${cisX - 80} Z`}
                fill="rgba(0,194,255,0.30)"
              />
              <path
                className="water wave2"
                d={`M ${cisX - 80} ${groundY + cisternH * 0.46} ${'q 20 -6 40 0 t 40 0 '.repeat(8)}V ${groundY + cisternH} H ${cisX - 80} Z`}
                fill="rgba(125,211,252,0.22)"
              />
            </g>
            <text
              x={cisX + cisW / 2}
              y={groundY + cisternH / 2 + 4}
              textAnchor="middle"
              fill="#00C2FF"
              fontFamily="JetBrains Mono"
              fontSize="12"
            >
              {cisternaM3} m³
            </text>
            <text
              x={cisX + cisW / 2}
              y={groundY + cisternH + 14}
              textAnchor="middle"
              fill="#00C2FF"
              fontFamily="JetBrains Mono"
              fontSize="9"
              letterSpacing="1.5"
            >
              CISTERNA
            </text>

            {/* Impulsión */}
            <line
              x1={cisX + cisW / 2}
              y1={groundY + cisternH / 2}
              x2={cisX + cisW / 2}
              y2={pad.t + azoteaH - tanqueH / 2}
              stroke="#00C2FF"
              strokeWidth="1.5"
              strokeDasharray="4 3"
              markerEnd="url(#arrUp)"
            />
            <text
              x={cisX + cisW / 2 + 6}
              y={groundY - 6}
              fill="#00C2FF"
              fontFamily="JetBrains Mono"
              fontSize="8"
              letterSpacing="1.4"
            >
              IMPULSIÓN
            </text>

            <text
              x={VW / 2}
              y={VH - 8}
              textAnchor="middle"
              fill="rgba(184,207,224,0.4)"
              fontFamily="JetBrains Mono"
              fontSize="9"
              letterSpacing="1.2"
            >
              IS.010 §2.4 · Cisterna ≥ ¾ dotación · Tanque elevado ≥ ⅓ dotación
            </text>
          </svg>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// PressureCard — estática en pisos extremos (§2.3)
// ──────────────────────────────────────────────────────────────────

function PressureCard({ pressure }: { pressure: BuildingPressureResult }): JSX.Element {
  const recommendationText = (() => {
    switch (pressure.recommendation) {
      case 'hidroneumatico':
        return '⚠ Último piso < 2 mca — considera HIDRONEUMÁTICO para garantizar presión.';
      case 'reductora':
        return '⚠ Primer piso > 50 mca — instala REDUCTORA de presión en pisos bajos.';
      case 'ambas':
        return '⚠ Sistema desbalanceado — requiere HIDRONEUMÁTICO arriba y REDUCTORA abajo.';
      case null:
        return '✓ Sistema dentro del rango §2.3 (2–50 mca en todos los pisos).';
    }
  })();
  const recClass =
    pressure.recommendation === null ? 'pres-rec-ok' : 'pres-rec-warn';

  return (
    <div className="ac-result-card" style={{ marginTop: 12 }}>
      <div className="ac-card-h">
        <span className="ac-dot" style={{ background: 'var(--dim-col)' }} />
        PRESIÓN ESTÁTICA · IS.010 §2.3
      </div>
      <div className="kv ac">
        <span className="k">Último piso (peor caso)</span>
        <span className={`v ${pressure.ultimoPisoOk ? 't-ok' : 't-err'}`}>
          {pressure.ultimoPiso_mca.toFixed(2)} mca
        </span>
      </div>
      <div className="kv ac">
        <span className="k">Primer piso (mayor estática)</span>
        <span className={`v ${pressure.primerPisoOk ? 't-ok' : 't-err'}`}>
          {pressure.primerPiso_mca.toFixed(2)} mca
        </span>
      </div>
      <div className={`pres-rec ${recClass}`}>{recommendationText}</div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// CatalogFloater — ficha técnica de depósito + selección por marca
// (portado de extras.jsx → CatalogFloater)
// ──────────────────────────────────────────────────────────────────

function capLabel(cap: number | string): string {
  return typeof cap === 'number' ? `${cap} m³` : cap;
}
function capLitros(cap: number | string): string {
  return typeof cap === 'number' ? `${Math.round(cap * 1000)} L` : cap;
}

/**
 * Gráfico grande del depósito en la ficha técnica (porteado del diseño
 * aprobado). Representa visualmente la diferencia entre cisterna (debajo
 * del suelo), tanque elevado (sobre estructura) y terma (calentador
 * cilíndrico vertical).
 */
function DepositoGraphic({ tipo }: { tipo: DepositoTipo }): JSX.Element {
  if (tipo === 'cisterna') {
    return (
      <svg viewBox="0 0 200 110" className="dg-svg">
        {/* Suelo */}
        <line x1="10" y1="40" x2="190" y2="40" stroke="#2A4060" strokeWidth="1.4" />
        <text x="14" y="36" fill="#2A4060" fontFamily="JetBrains Mono" fontSize="8">NIVEL ±0.00</text>
        {/* Cisterna (enterrada) */}
        <rect x="40" y="40" width="120" height="55" fill="rgba(0,194,255,0.06)" stroke="#00C2FF" strokeWidth="1.6" />
        {/* Olas */}
        <path d="M 45 60 q 10 -4 20 0 t 20 0 t 20 0 t 20 0 t 20 0 V 95 H 45 Z" fill="rgba(0,194,255,0.30)" />
        <path d="M 45 66 q 10 -3 20 0 t 20 0 t 20 0 t 20 0 t 20 0 V 95 H 45 Z" fill="rgba(125,211,252,0.22)" />
        {/* Tapa */}
        <rect x="86" y="36" width="28" height="6" fill="#0B1422" stroke="#00C2FF" strokeWidth="1.2" />
        <text x="100" y="32" textAnchor="middle" fill="#00C2FF" fontFamily="JetBrains Mono" fontSize="7">TAPA</text>
        <text x="100" y="78" textAnchor="middle" fill="#00C2FF" fontFamily="JetBrains Mono" fontSize="9">CISTERNA</text>
      </svg>
    );
  }
  if (tipo === 'tanque') {
    return (
      <svg viewBox="0 0 200 110" className="dg-svg">
        {/* Estructura/torre */}
        <line x1="50" y1="100" x2="50" y2="60" stroke="#FF4040" strokeWidth="1.4" />
        <line x1="150" y1="100" x2="150" y2="60" stroke="#FF4040" strokeWidth="1.4" />
        <line x1="50" y1="80" x2="150" y2="80" stroke="#FF4040" strokeWidth="0.8" strokeDasharray="3 3" />
        <line x1="40" y1="100" x2="160" y2="100" stroke="#FF4040" strokeWidth="1.4" />
        {/* Tanque (cilindro) */}
        <ellipse cx="100" cy="20" rx="50" ry="6" fill="rgba(255,64,64,0.10)" stroke="#FF4040" strokeWidth="1.4" />
        <path d="M 50 20 V 60 a 50 6 0 0 0 100 0 V 20" fill="rgba(255,64,64,0.06)" stroke="#FF4040" strokeWidth="1.6" />
        <path d="M 55 40 q 10 -3 20 0 t 20 0 t 20 0 t 20 0 V 56 a 45 4 0 0 1 −90 0 Z" fill="rgba(125,211,252,0.30)" />
        <text x="100" y="48" textAnchor="middle" fill="#FF4040" fontFamily="JetBrains Mono" fontSize="9">TANQUE ELEVADO</text>
      </svg>
    );
  }
  // Terma
  return (
    <svg viewBox="0 0 200 110" className="dg-svg">
      {/* Muro */}
      <line x1="20" y1="0" x2="20" y2="110" stroke="#2A4060" strokeWidth="1.4" />
      {/* Cuerpo de terma */}
      <rect x="80" y="10" width="50" height="95" fill="rgba(244,162,97,0.08)" stroke="#F4A261" strokeWidth="1.6" rx="3" />
      {/* Indicador de temperatura */}
      <circle cx="105" cy="32" r="6" fill="rgba(244,162,97,0.20)" stroke="#F4A261" strokeWidth="1.2" />
      <text x="105" y="35" textAnchor="middle" fill="#F4A261" fontFamily="JetBrains Mono" fontSize="7">°C</text>
      {/* Salida AC arriba (rojo) */}
      <line x1="105" y1="10" x2="105" y2="2" stroke="#FF4040" strokeWidth="1.8" />
      {/* Entrada AF abajo (azul) */}
      <line x1="120" y1="105" x2="120" y2="115" stroke="#00C2FF" strokeWidth="1.8" />
      <text x="155" y="58" fill="#F4A261" fontFamily="JetBrains Mono" fontSize="9">TERMA</text>
      <text x="155" y="70" fill="#F4A261" fontFamily="JetBrains Mono" fontSize="7" opacity="0.7">eléctrica/</text>
      <text x="155" y="78" fill="#F4A261" fontFamily="JetBrains Mono" fontSize="7" opacity="0.7">a gas</text>
    </svg>
  );
}

function CatalogFloater({
  tipo,
  selectedId,
  required,
  onSelect,
  onClose,
}: {
  tipo: DepositoTipo;
  selectedId: string;
  required: number;
  onSelect: (id: string) => void;
  onClose: () => void;
}): JSX.Element {
  const list = CATALOGO_DEPOSITOS[tipo];
  const cur: DepositoItem = list.find((x) => x.id === selectedId) ?? list[0];
  const sysClass = tipo === 'cisterna' ? 'af' : tipo === 'tanque' ? 'ac' : 'mid';
  const meetsReq = required > 0 ? typeof cur.cap === 'number' && cur.cap >= required : true;

  return (
    <div className="catalog-floater">
      <div className="cf-hd">
        <span className={`cf-dot ${sysClass}`} />
        <span className="cf-title">{tipo.toUpperCase()} — FICHA TÉCNICA</span>
        <button className="cf-close" onClick={onClose}>
          ✕
        </button>
      </div>
      {/* Gráfico grande del depósito (cistern/tank/terma) — porteado del
          diseño aprobado. Reemplaza la fila pequeña del icono en la lista
          de propiedades de abajo (que se queda como referencia textual). */}
      <div className={`cf-graphic ${sysClass}`}>
        <DepositoGraphic tipo={tipo} />
      </div>
      <div className="cf-info">
        <div className="cf-row">
          <span className="k">
            <span className="cf-ic">
              {tipo === 'cisterna'
                ? DotIcon.cistern
                : tipo === 'tanque'
                  ? DotIcon.tank
                  : DotIcon.terma}
            </span>
            Modelo
          </span>
          <span className="v">{cur.modelo}</span>
        </div>
        <div className="cf-row">
          <span className="k">
            <span className="cf-ic">{DotIcon.building}</span>Marca
          </span>
          <span className="v">{cur.marca}</span>
        </div>
        <div className="cf-row">
          <span className="k">
            <span className="cf-ic">{DotIcon.drop}</span>Capacidad
          </span>
          <span className="v">{capLabel(cur.cap)}</span>
        </div>
        <div className="cf-row">
          <span className="k">
            <span className="cf-ic">{DotIcon.metric}</span>Dimensiones
          </span>
          <span className="v">{cur.dim}</span>
        </div>
        <div className="cf-row">
          <span className="k">
            <span className="cf-ic">{DotIcon.area}</span>Material
          </span>
          <span className="v">{cur.mat}</span>
        </div>
        {required > 0 && (
          <div className={`cf-req ${meetsReq ? 'ok' : 'warn'}`}>
            {meetsReq ? '✓' : '⚠'} Requerido por cálculo: {required} m³ ·
            {meetsReq ? ' OK' : ' insuficiente'}
          </div>
        )}
      </div>
      <div className="cf-list">
        {list.map((it) => (
          <button
            key={it.id}
            className={`cf-item ${it.id === selectedId ? 'cur' : ''}`}
            onClick={() => onSelect(it.id)}
          >
            <span className="cfi-marca">{it.marca}</span>
            <span className="cfi-cap">{capLitros(it.cap)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// FormulaPanel — "Ver fórmula aplicada"
// ──────────────────────────────────────────────────────────────────

interface FormulaPanelProps {
  tipo: TipoEdificacion;
  dorm: number;
  area: number;
  totalViviendas: number;
  dotacionAF: number;
  cisternaL: number;
  tanqueL: number;
  dotacionAC: number;
  termaL: number;
}

/**
 * Muestra las tablas normativas IS.010 con la fila aplicada iluminada,
 * más las fórmulas de §2.4 (almacenamiento) y §3.4 (terma) resueltas con
 * los valores del cálculo actual.
 */
function FormulaPanel({
  tipo,
  dorm,
  area,
  totalViviendas,
  dotacionAF,
  cisternaL,
  tanqueL,
  dotacionAC,
  termaL,
}: FormulaPanelProps): JSX.Element {
  return (
    <div className="src-table">
      {/* §2.2 — Dotación AF */}
      <div className="src-block">
        <h4>
          <span className="src-norm">IS.010 §2.2</span> · Dotación AF{' '}
          {tipo === 'multi' ? '(multifamiliar)' : '(unifamiliar)'}
        </h4>
        {tipo === 'multi' ? (
          <table className="src-tbl">
            <thead>
              <tr>
                <th>Dormitorios / dpto</th>
                <th>L / dpto · día</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(DOTACION_AF_MULTIFAMILIAR).map(([d, val]) => (
                <tr key={d} className={Number(d) === dorm ? 'src-hit' : ''}>
                  <td>{d}</td>
                  <td>{val}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table className="src-tbl">
            <thead>
              <tr>
                <th>Área del lote (m²)</th>
                <th>L / día</th>
              </tr>
            </thead>
            <tbody>
              {DOTACION_AF_UNIFAMILIAR.map((row, i) => {
                // hit = primer row cuyo areaMaxM2 cubre el area declarada
                const prev =
                  i === 0 ? 0 : DOTACION_AF_UNIFAMILIAR[i - 1].areaMaxM2;
                const hit = area > prev && area <= row.areaMaxM2;
                const label =
                  row.areaMaxM2 === Infinity ? '> 700' : `≤ ${row.areaMaxM2}`;
                return (
                  <tr key={i} className={hit ? 'src-hit' : ''}>
                    <td>{label}</td>
                    <td>{row.dotacionL}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <div className="src-foot">
          {tipo === 'multi' ? (
            <>
              D<sub>AF</sub> = {DOTACION_AF_MULTIFAMILIAR[dorm] ?? 0} L/dpto ×{' '}
              {totalViviendas} dpto = <b className="t-af">{dotacionAF} L/día</b>
            </>
          ) : (
            <>
              D<sub>AF</sub> según área {area} m² ={' '}
              <b className="t-af">{dotacionAF} L/día</b>
            </>
          )}
        </div>
      </div>

      {/* §2.4 — Almacenamiento */}
      <div className="src-block">
        <h4>
          <span className="src-norm">IS.010 §2.4</span> · Almacenamiento
        </h4>
        <div className="src-foot">
          Cisterna ≥ ¾ × D<sub>AF</sub> = ¾ × {dotacionAF} ={' '}
          <b className="t-af">{cisternaL} L</b> (mínimo 1 000 L)
          <br />
          Tanque elevado ≥ ⅓ × D<sub>AF</sub> = ⅓ × {dotacionAF} ={' '}
          <b className="t-af">{tanqueL} L</b> (mínimo 1 000 L)
        </div>
      </div>

      {/* §3.2 — Dotación AC */}
      <div className="src-block">
        <h4>
          <span className="src-norm">IS.010 §3.2</span> · Dotación AC por
          vivienda
        </h4>
        <table className="src-tbl">
          <thead>
            <tr>
              <th>Dormitorios</th>
              <th>L / día</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(DOTACION_AC).map(([d, val]) => (
              <tr key={d} className={Number(d) === dorm ? 'src-hit' : ''}>
                <td>{d}</td>
                <td>{val}</td>
              </tr>
            ))}
            {dorm > 5 && (
              <tr className="src-hit">
                <td>{dorm} (extra)</td>
                <td>{450 + (dorm - 5) * DOTACION_AC_EXTRA_POR_DORM}</td>
              </tr>
            )}
          </tbody>
        </table>
        <div className="src-foot ac">
          D<sub>AC</sub> = <b className="t-ac">{dotacionAC} L/día</b>
          {dorm > 5 && (
            <>
              {' '}
              · fórmula extra: 450 + ({dorm} − 5) × {DOTACION_AC_EXTRA_POR_DORM}
            </>
          )}
        </div>
      </div>

      {/* §3.4 — Terma */}
      <div className="src-block">
        <h4>
          <span className="src-norm">IS.010 §3.4</span> · Capacidad terma
        </h4>
        <div className="src-foot ac">
          Tanque ≥ ⅕ × D<sub>AC</sub> = ⅕ × {dotacionAC} ={' '}
          <b className="t-ac">{termaL} L</b>
        </div>
      </div>
    </div>
  );
}
